import { Ionicons } from '@expo/vector-icons';
import React, { useMemo } from 'react';
import { View } from 'react-native';

import { MemberAvatar } from '@/components/team/member-avatar';
import { ThemedText } from '@/components/themed-text';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { FadeInView } from '@/components/ui/fade-in-view';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SkeletonCard } from '@/components/ui/skeleton';
import { useTeamReport } from '@/hooks/use-team';
import { useTerms } from '@/hooks/use-terms';
import { TeamReportMember } from '@/types/organization';
import { formatCurrency } from '@/utils/format-currency';

/**
 * Visão "Equipe" dos Relatórios: o recebido da organização no período e uma
 * linha por profissional (recebido, atendimentos, clientes). Tocar numa linha
 * troca o foco para a pessoa e volta para a visão individual dela.
 */
export function TeamReportView({
  from,
  to,
  onOpenMember,
}: {
  from: string;
  to: string;
  onOpenMember: (member: TeamReportMember) => void;
}) {
  const terms = useTerms();
  const { data, isLoading, error, refetch } = useTeamReport(from, to);

  const rows = useMemo(
    () =>
      [...(data?.members ?? [])].sort(
        (a, b) => b.revenue - a.revenue || b.appointments - a.appointments,
      ),
    [data?.members],
  );

  if (isLoading) {
    return (
      <View className="gap-3">
        <SkeletonCard showHeader lines={2} />
        <SkeletonCard showHeader lines={3} />
      </View>
    );
  }

  if (error || !data) {
    return (
      <ErrorState
        variant="inline"
        title="Não foi possível carregar"
        message="Tente de novo em instantes."
        onRetry={refetch}
      />
    );
  }

  const { totals } = data;
  if (totals.revenue === 0 && totals.pending === 0 && totals.appointments === 0) {
    return (
      <Card>
        <EmptyState
          icon="bar-chart-outline"
          title="Sem movimento no período"
          description={`Quando a equipe atender, os números de cada ${terms.professional} aparecem aqui.`}
          className="py-10"
        />
      </Card>
    );
  }

  const count = (n: number, singular: string, plural: string) =>
    `${n} ${n === 1 ? singular : plural}`;

  return (
    <>
      <FadeInView delay={0}>
        <View
          className="w-full gap-5 rounded-3xl bg-ink p-5"
          style={{ borderCurve: 'continuous', boxShadow: '0 12px 32px rgba(10, 10, 10, 0.25)' }}
        >
          <ThemedText className="font-medium text-sm text-white/60">
            Recebido pela equipe
          </ThemedText>
          <ThemedText
            className="font-bold text-4xl text-white"
            style={{ fontVariant: ['tabular-nums'] }}
            numberOfLines={1}
            adjustsFontSizeToFit
          >
            {formatCurrency(totals.revenue)}
          </ThemedText>
          <View className="flex-row gap-3">
            <HeroTile label="A receber" value={formatCurrency(totals.pending)} />
            <HeroTile label={terms.title.appointmentPlural} value={String(totals.appointments)} />
            <HeroTile label={terms.title.clientPlural} value={String(totals.clientsServed)} />
          </View>
        </View>
      </FadeInView>

      <FadeInView delay={80}>
        <Card>
          <CardHeader>
            <CardTitle>{`Por ${terms.professional}`}</CardTitle>
          </CardHeader>
          <CardContent>
            {rows.map((m, index) => {
              const share = totals.revenue > 0 ? (m.revenue / totals.revenue) * 100 : 0;
              const isOwner = m.role === 'OWNER';
              // Desativado não abre: o foco nele seria recusado pela API.
              const tappable = isOwner || m.status === 'ACTIVE';
              const details = [
                count(m.appointments, terms.appointment, terms.appointmentPlural),
                count(m.clientsServed, terms.client, terms.clientPlural),
                m.cancelledAppointments > 0
                  ? `${m.cancelledAppointments} ${terms.agree(
                      m.cancelledAppointments === 1 ? 'cancelado' : 'cancelados',
                      'appointment',
                    )}`
                  : null,
                m.status === 'DISABLED' ? 'desativado' : m.status === 'REMOVED' ? 'excluído' : null,
              ].filter(Boolean);

              return (
                <PressableScale
                  key={m.providerId}
                  haptic={tappable}
                  disabled={!tappable}
                  onPress={() => onOpenMember(m)}
                  accessibilityRole="button"
                  accessibilityLabel={m.name}
                  className={`flex-row items-center gap-3 py-3 ${
                    index > 0 ? 'border-t border-neutral-100' : ''
                  }`}
                >
                  <MemberAvatar name={m.name} image={m.image} color={m.color} size={40} />
                  <View className="min-w-0 flex-1 gap-1.5">
                    <View className="flex-row items-baseline justify-between gap-3">
                      <ThemedText
                        numberOfLines={1}
                        className="shrink font-semibold text-[15px] text-neutral-900"
                      >
                        {isOwner ? `${m.name} · você` : m.name}
                      </ThemedText>
                      <ThemedText
                        className="font-semibold text-[15px] text-neutral-900"
                        style={{ fontVariant: ['tabular-nums'] }}
                      >
                        {formatCurrency(m.revenue)}
                      </ThemedText>
                    </View>
                    <ThemedText numberOfLines={1} className="text-xs text-neutral-500">
                      {details.join(' · ')}
                    </ThemedText>
                    <View className="h-1.5 overflow-hidden rounded-full bg-neutral-100">
                      <View
                        className="h-full rounded-full"
                        style={{ width: `${share}%`, backgroundColor: m.color ?? '#FD5001' }}
                      />
                    </View>
                  </View>
                  {tappable ? <Ionicons className="text-neutral-400" name="chevron-forward" size={18} /> : null}
                </PressableScale>
              );
            })}
          </CardContent>
        </Card>
      </FadeInView>
    </>
  );
}

function HeroTile({ label, value }: { label: string; value: string }) {
  return (
    <View className="flex-1 gap-1 rounded-2xl bg-white/10 p-3">
      <ThemedText numberOfLines={1} className="font-medium text-xs text-white/60">
        {label}
      </ThemedText>
      <ThemedText
        className="font-semibold text-base text-white"
        style={{ fontVariant: ['tabular-nums'] }}
        numberOfLines={1}
        adjustsFontSizeToFit
      >
        {value}
      </ThemedText>
    </View>
  );
}
